import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';

const Features = () => {
  const { user, userProfile, isAuthenticated } = useAuth();

  const featureCategories = [
    {
      category: 'Interactive Learning',
      icon: 'BookOpen',
      color: 'from-blue-500 to-cyan-500',
      features: [
        {
          icon: 'MousePointerClick',
          title: 'Interactive Script Reading',
          description: 'Click any word for instant definitions, historical context, and modern translations without losing your place in the text.'
        },
        {
          icon: 'Volume2',
          title: 'Audio Pronunciation Guide',
          description: 'Hear professional recordings of difficult words and phrases, with side-by-side comparison to perfect your Shakespearean speech.'
        },
        {
          icon: 'Library',
          title: 'Contextual Glossary',
          description: 'Explore comprehensive word definitions with usage examples, etymology, and connections to other Shakespeare works.'
        }
      ]
    },
    {
      category: 'Gamification & Progress',
      icon: 'Trophy',
      color: 'from-purple-500 to-pink-500',
      features: [
        {
          icon: 'Zap',
          title: 'XP & Level System',
          description: 'Earn experience points for reading, answering questions correctly, and completing challenges. Watch your level grow!'
        },
        {
          icon: 'Award',
          title: 'Achievement Badges',
          description: 'Unlock 50+ unique badges for milestones like completing plays, maintaining streaks, and mastering difficult concepts.'
        },
        {
          icon: 'TrendingUp',
          title: 'Progress Visualization',
          description: 'Track your journey with beautiful charts showing reading progress, comprehension scores, and skill development over time.'
        }
      ]
    },
    {
      category: 'Assessment & Practice',
      icon: 'GraduationCap',
      color: 'from-green-500 to-emerald-500',
      features: [
        {
          icon: 'ClipboardCheck',
          title: 'Comprehensive Quizzes',
          description: 'Test understanding with multiple question types: reading comprehension, character analysis, and story structure questions.'
        },
        {
          icon: 'Mic',
          title: 'Pronunciation Practice',
          description: 'Record yourself reading passages and receive instant AI-powered feedback on pronunciation accuracy and fluency.'
        },
        {
          icon: 'RefreshCw',
          title: 'Adaptive Learning',
          description: 'AI adjusts difficulty based on your performance, ensuring you\'re always challenged at the right level for optimal growth.'
        }
      ]
    },
    {
      category: 'Collaboration & Social',
      icon: 'Users',
      color: 'from-orange-500 to-red-500',
      features: [
        {
          icon: 'MessageCircle',
          title: 'Class Discussions',
          description: 'Engage with classmates through moderated discussion boards, share insights, and learn from different perspectives.'
        },
        {
          icon: 'Medal',
          title: 'Leaderboards',
          description: 'Compete with your class through friendly competition, motivating everyone to improve and excel together.'
        },
        {
          icon: 'UserPlus',
          title: 'Study Groups',
          description: 'Form study groups with friends, collaborate on challenges, and support each other\'s learning journey.'
        }
      ]
    }
  ];

  const platformCapabilities = [
    {
      icon: 'Smartphone',
      title: 'Mobile Responsive',
      description: 'Access BardPlay seamlessly on any device - desktop, tablet, or smartphone'
    },
    {
      icon: 'Cloud',
      title: 'Cloud Sync',
      description: 'Your progress automatically syncs across all devices, so you can learn anywhere'
    },
    {
      icon: 'Shield',
      title: 'Privacy First',
      description: 'Student data is protected with industry-leading security and COPPA compliance'
    },
    {
      icon: 'Wifi',
      title: 'Works Offline',
      description: 'Download plays for offline reading when you don\'t have internet access'
    },
    {
      icon: 'Globe',
      title: 'Multi-Language',
      description: 'Interface available in multiple languages to support diverse learners'
    },
    {
      icon: 'Accessibility',
      title: 'Accessible Design',
      description: 'Built with accessibility in mind, supporting screen readers and keyboard navigation'
    }
  ];

  const educatorTools = [
    {
      icon: 'LayoutDashboard',
      title: 'Teacher Dashboard',
      description: 'Monitor student progress, identify struggling learners, and celebrate achievements with comprehensive analytics.'
    },
    {
      icon: 'ClipboardList',
      title: 'Custom Assignments',
      description: 'Create tailored assignments, set deadlines, and track completion rates for individual students or entire classes.'
    },
    {
      icon: 'FileText',
      title: 'Automated Grading',
      description: 'Save time with automatic quiz grading and detailed performance reports for every student.'
    },
    {
      icon: 'BarChart3',
      title: 'Analytics & Insights',
      description: 'Understand class trends, identify common misconceptions, and adapt teaching strategies with data-driven insights.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Features - BardPlay</title>
        <meta name="description" content="Explore BardPlay's comprehensive features including interactive learning, gamification, assessments, collaboration tools, and educator dashboards for mastering Shakespeare." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Hero Section */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6 border border-primary/20">
                <Icon name="Sparkles" size={20} className="text-primary" />
                <span className="text-primary font-semibold">Powerful Features for Modern Learning</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Everything You Need to Master <span className="text-gradient">Shakespeare</span>
              </h1>
              <p className="text-xl text-white/70 leading-relaxed">
                Discover the comprehensive suite of tools, features, and capabilities that make BardPlay the ultimate platform for Shakespeare education.
              </p>
            </div>
          </div>

          {/* Feature Categories */}
          {featureCategories?.map((category, catIndex) => (
            <div key={catIndex} className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
              <div className="mb-12">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${category?.color} flex items-center justify-center`}>
                    <Icon name={category?.icon} size={24} className="text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white">
                    {category?.category}
                  </h2>
                </div>
                <div className="h-1 w-32 bg-gradient-to-r from-primary to-transparent rounded-full"></div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                {category?.features?.map((feature, index) => (
                  <div 
                    key={index}
                    className="group bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon name={feature?.icon} size={28} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{feature?.title}</h3>
                    <p className="text-white/70 leading-relaxed">{feature?.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Platform Capabilities */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl p-8 md:p-12 border border-white/10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Platform Capabilities
                </h2>
                <p className="text-white/70 max-w-2xl mx-auto">
                  Built with modern technology to provide the best learning experience
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {platformCapabilities?.map((capability, index) => (
                  <div 
                    key={index}
                    className="bg-surface-dark/80 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300 flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name={capability?.icon} size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">{capability?.title}</h3>
                      <p className="text-white/60 text-sm">{capability?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Educator Tools */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Powerful Tools for Educators
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Comprehensive features designed to support teachers and enhance classroom management
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {educatorTools?.map((tool, index) => (
                <div 
                  key={index}
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-primary/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name={tool?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-3">{tool?.title}</h3>
                      <p className="text-white/70 leading-relaxed">{tool?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl p-8 md:p-12 border border-white/10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 animate-pulse"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                  <Icon name="Rocket" size={40} className="text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Experience All Features for Free
                </h2>
                <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                  Start using BardPlay today and discover how our comprehensive feature set transforms Shakespeare education. No credit card required.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/registration"
                    className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
                  >
                    Get Started Free
                    <Icon name="ArrowRight" size={20} />
                  </Link>
                  <Link 
                    to="/contact-us"
                    className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors duration-300 border border-white/20"
                  >
                    Schedule Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Features;